import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  consentCell,
  consentOverview,
  consentRecords,
  consentWrite,
  residentDataFlows,
  type ApiError,
  type ConsentOverviewResponse,
} from '@aithora/shared';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { type ResidentsCtx, useApi } from './common';
import { consentVals, defaultResident, type ConsentUi } from './consentVals';
import { ConsentView } from './ConsentView';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';

// 'aiInteraction' is the data-collection grant the server enforces (src/aithora/consent.ts).
const GRANT_TYPE = ['aiInteraction', 'model_training'] as const;
/** The history plays one quarter per step (design kPlay, 620 ms). */
export const PLAY_STEP_MS = 620;

/** Consent (design rIsConsent, T3355). */
export function ConsentTab({ ctx }: { ctx: ResidentsCtx }) {
  const navigate = useNavigate();
  const pick = facilitiesParam(usePickedIds());
  const overview = useApi<ConsentOverviewResponse>((c, signal) => consentOverview(c, { query: pick ? { facilities: pick } : undefined, signal }), [pick]);
  const [ui, setUiState] = useState<ConsentUi>({ sel: null, view: 'net', sim: null, asOf: null, focus: null, pend: null, playing: false, prop: null });
  const [err, setErr] = useState<ApiError | null>(null);
  const setUi = (p: Partial<ConsentUi>) => setUiState((s) => ({ ...s, ...p }));
  const res = overview.data?.residents ?? [];
  const selId = ui.sel ?? defaultResident(res);
  const sel = res.find((r) => r.residentId === selId) ?? null;
  const flows = useApi((c, signal) => residentDataFlows(c, { params: { id: selId! }, signal }), [selId], { enabled: !!selId });
  const records = useApi((c, signal) => consentRecords(c, { query: { userId: selId!, organizationId: sel!.organizationId }, signal }), [selId], { enabled: !!sel });
  const timer = useRef<number | null>(null);
  const quarters = flows.data?.quarters ?? ['now'];

  useEffect(() => () => { if (timer.current) window.clearInterval(timer.current); }, []);
  const togglePlay = () => {
    if (timer.current) {
      window.clearInterval(timer.current);
      timer.current = null;
      setUi({ playing: false });
      return;
    }
    let n = 0;
    setUi({ playing: true, asOf: 0 });
    timer.current = window.setInterval(() => {
      n += 1;
      if (n >= quarters.length - 1) {
        if (timer.current) window.clearInterval(timer.current);
        timer.current = null;
        setUi({ playing: false, asOf: null });
        return;
      }
      setUi({ asOf: n });
    }, PLAY_STEP_MS);
  };

  if (overview.loading && !overview.data && !overview.error) return <Skeleton rows={6} label="Loading consent" />;
  if (!overview.data) {
    return overview.error?.status === 404 ? (
      <EmptyState title="Consent moves here when the back end lists every resident's grants" body="Each resident's two grants, who gave them, and who reads the record." waitingOn="GET /residents/consent-overview" />
    ) : overview.error ? <ErrorState error={overview.error} onRetry={overview.reload} /> : null;
  }
  const V = consentVals({
    residents: res,
    flows: flows.data,
    records: records.data?.records ?? [],
    ui,
    setUi,
    canWrite: ctx.canAct,
    togglePlay,
    openException: () => navigate('/worklist'),
    grant: (residentId, i, on) => {
      const r = res.find((x) => x.residentId === residentId);
      if (!r) return;
      setErr(null);
      // stay on this resident while the lists re-read
      setUi({ sel: residentId });
      // A withdrawal is confirm-first (the inline "Sign and withdraw"); the server gets confirm: true.
      consentWrite(ctx.client, { body: { userId: residentId, organizationId: r.organizationId, consentType: GRANT_TYPE[i], granted: on, confirm: on ? undefined : true } })
        .then((out) => {
          if (!on && out.propagation?.length) setUi({ prop: { key: residentId + ':' + i, steps: out.propagation } });
          overview.reload(); records.reload(); flows.reload();
        })
        .catch((e: ApiError) => setErr(e));
    },
    cell: (recipient, ci, shared) => {
      if (!selId) return;
      setErr(null);
      consentCell(ctx.client, { body: { residentId: selId, recipient, dataClass: flows.data?.classes[ci] ?? String(ci), shared } })
        .then(() => flows.reload())
        .catch((e: ApiError) => setErr(e));
    },
  });
  return (
    <>
      <ConsentView V={{ ...V, rIsConsent: true }} />
      {(err || flows.error) && <ErrorState error={(err ?? flows.error)!} onRetry={err ? undefined : flows.reload} />}
    </>
  );
}
